/**
 * La cuenta del comprador: sus códigos, su exportación y su borrado (#46).
 *
 * Los dos derechos están en la misma tarjeta y con el mismo peso visual que
 * el resto. No es cortesía: el derecho de acceso y el de supresión son
 * exigibles bajo GDPR, LGPD y la Ley 6534/2020, y una exportación que hay que
 * pedir por correo no cumple. Mostrarlos, además, es lo que hace que alguien
 * confíe en dejar sus datos.
 *
 * **Borrar la cuenta borra de verdad**: la fila, sus sesiones, sus
 * consentimientos y sus códigos guardados (`DELETE /consumer/portal/account`).
 * No es un `is_active = false` — un borrado lógico dejaría el correo y la
 * lista de códigos escaneados en la tabla, que es exactamente el dato que la
 * persona pidió que no exista más. Lo que desaparece es **la asociación entre
 * la persona y los escaneos**; el escaneo en sí es un evento operativo de la
 * marca y no lleva identidad.
 *
 * Por eso las dos acciones destructivas pasan por `ConfirmDialog`, y la del
 * borrado de cuenta exige tipear la palabra: no se deshace.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import type { ApiError } from '@/api/errors';
import { consumerPortalKeys } from '@/api/keys';
import type { Page } from '@/api/types';
import type { MessageKey } from '@/i18n/messages';
import { useI18n } from '@/i18n/useI18n';
import { Badge, type Tone } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { ConfirmDialog } from '../admin/ConfirmDialog';
import {
  portalCodes,
  portalDeleteAccount,
  portalDeleteCode,
  portalExportData,
  portalLogout,
  portalMe,
  portalSaveCode,
  type PortalAccount,
  type PortalDataExport,
  type PortalSession,
  type SavedCode,
} from './portalApi';

const PAGE_SIZE = 25;

/** Los tres resultados que `SaveCodeIn` admite. */
const RESULT_LABELS: Record<string, MessageKey> = {
  genuine: 'portal.result.genuine',
  already_seen: 'portal.result.already_seen',
  recalled: 'portal.result.recalled',
};

const RESULT_TONES: Record<string, Tone> = {
  genuine: 'verified',
  already_seen: 'neutral',
  recalled: 'critical',
};

export function PortalAccountPanel({
  session,
  onClosed,
}: {
  session: PortalSession;
  onClosed: () => void;
}): ReactNode {
  const { t, fmt } = useI18n();
  const queryClient = useQueryClient();
  const token = session.token;
  const [deleted, setDeleted] = useState(false);

  const account = useQuery<PortalAccount, ApiError>({
    queryKey: consumerPortalKeys.me(),
    queryFn: ({ signal }) => portalMe(token, signal),
    initialData: session.account,
  });

  const codes = useInfiniteQuery<
    Page<SavedCode>,
    ApiError,
    { pages: Page<SavedCode>[] },
    QueryKey,
    string | null
  >({
    queryKey: consumerPortalKeys.codes(PAGE_SIZE),
    queryFn: ({ pageParam, signal }) =>
      portalCodes(token, { limit: PAGE_SIZE, cursor: pageParam }, signal),
    initialPageParam: null,
    getNextPageParam: (last) => last.next_cursor,
  });

  const items = useMemo(
    () => (codes.data?.pages ?? []).flatMap((page) => page.items),
    [codes.data],
  );

  const forgetSession = (): void => {
    queryClient.removeQueries({ queryKey: consumerPortalKeys.me() });
    queryClient.removeQueries({ queryKey: consumerPortalKeys.codes(PAGE_SIZE) });
  };

  const logout = useMutation<unknown, ApiError>({
    mutationFn: () => portalLogout(token),
    onSettled: () => {
      // Aunque el logout falle, la sesión local se suelta: dejarla viva
      // porque el servidor no contestó es peor que soltarla de más.
      forgetSession();
      onClosed();
    },
  });

  const removeAccount = useMutation<unknown, ApiError>({
    mutationFn: () => portalDeleteAccount(token),
    onSuccess: () => {
      forgetSession();
      setDeleted(true);
    },
  });

  if (deleted) {
    return (
      <Card>
        <CardHeader title={t('portal.delete.title')} />
        <CardBody className="space-y-3">
          <p className="max-w-prose text-sm text-ink" role="status">
            {t('portal.delete.done')}
          </p>
          <Button onClick={onClosed}>{t('confirm.accept')}</Button>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title={t('portal.account.title')}
          actions={
            <Button size="sm" variant="ghost" onClick={() => {logout.mutate();}} disabled={logout.isPending}>
              {t('portal.session.logout')}
            </Button>
          }
        />
        <CardBody>
          <dl className="grid gap-3 sm:grid-cols-3">
            <div>
              <dt className="text-xs tracking-wide text-ink-subtle uppercase">
                {t('portal.account.email')}
              </dt>
              <dd className="text-sm text-ink">{account.data.email}</dd>
            </div>
            <div>
              <dt className="text-xs tracking-wide text-ink-subtle uppercase">
                {t('portal.account.name')}
              </dt>
              <dd className="text-sm text-ink">{account.data.display_name ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs tracking-wide text-ink-subtle uppercase">
                {t('portal.account.created')}
              </dt>
              <dd className="text-sm text-ink">{fmt.dateTime(account.data.created_at)}</dd>
            </div>
          </dl>
        </CardBody>
      </Card>

      <SavedCodes
        token={token}
        items={items}
        isPending={codes.isPending}
        isError={codes.isError}
        error={codes.error}
        hasMore={codes.hasNextPage}
        isLoadingMore={codes.isFetchingNextPage}
        onLoadMore={() => {
          if (codes.hasNextPage && !codes.isFetchingNextPage) void codes.fetchNextPage();
        }}
        onRetry={() => {
          void codes.refetch();
        }}
      />

      <RightsCard
        token={token}
        onDeleteAccount={() => {
          removeAccount.mutate();
        }}
        deleting={removeAccount.isPending}
        deleteError={removeAccount.isError ? removeAccount.error : null}
      />
    </div>
  );
}

// ══════════════════════════════════════════════════════════ mis códigos

function SavedCodes({
  token,
  items,
  isPending,
  isError,
  error,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onRetry,
}: {
  token: string;
  items: SavedCode[];
  isPending: boolean;
  isError: boolean;
  error: ApiError | null;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
}): ReactNode {
  const { t, fmt } = useI18n();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: consumerPortalKeys.codes(PAGE_SIZE) });
  };

  const save = useMutation<SavedCode, ApiError>({
    mutationFn: () =>
      portalSaveCode(token, {
        code: code.trim().toUpperCase(),
        result: 'genuine',
        ...(label.trim() === '' ? {} : { label: label.trim() }),
      }),
    onSuccess: () => {
      setCode('');
      setLabel('');
      invalidate();
    },
  });

  const remove = useMutation<unknown, ApiError, string>({
    mutationFn: (ref: string) => portalDeleteCode(token, ref),
    onSuccess: () => {
      setPendingDelete(null);
      invalidate();
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    save.mutate();
  };

  return (
    <Card>
      <CardHeader title={t('portal.codes.title')} description={t('portal.codes.description')} />

      <CardBody className="border-b border-border">
        <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block text-ink-muted">{t('portal.codes.field.code')}</span>
            <input
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
              }}
              required
              minLength={20}
              maxLength={24}
              className="mt-1 w-64 rounded border border-border-strong bg-surface px-3 py-1.5 font-mono text-sm text-ink"
            />
          </label>
          <label className="text-sm">
            <span className="block text-ink-muted">{t('portal.codes.field.label')}</span>
            <input
              value={label}
              onChange={(event) => {
                setLabel(event.target.value);
              }}
              maxLength={80}
              className="mt-1 w-56 rounded border border-border-strong bg-surface px-3 py-1.5 text-sm text-ink"
            />
          </label>
          <Button type="submit" variant="primary" size="sm" disabled={save.isPending}>
            {save.isPending ? t('portal.codes.saving') : t('portal.codes.save')}
          </Button>
        </form>
        {save.isError && <ErrorState className="mt-3" error={save.error} />}
      </CardBody>

      {isPending && <LoadingState rows={4} />}

      {isError && error !== null && (
        <CardBody>
          <ErrorState error={error} onRetry={onRetry} />
        </CardBody>
      )}

      {!isPending && !isError && items.length === 0 && (
        <EmptyState title={t('portal.codes.empty')} />
      )}

      {items.length > 0 && (
        <>
          <Table caption={t('portal.codes.description')}>
            <thead>
              <tr>
                <Th>{t('portal.codes.column.ref')}</Th>
                <Th>{t('portal.codes.column.label')}</Th>
                <Th>{t('portal.codes.column.result')}</Th>
                <Th>{t('portal.codes.column.saved')}</Th>
                <Th>{t('portal.codes.column.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((saved) => (
                <tr key={saved.ref}>
                  {/* `ref` es una referencia opaca del servidor. El código
                      completo no se vuelve a mostrar y el `unit_id` no sale
                      nunca de la API. */}
                  <Td className="font-mono text-ink-muted">{saved.ref}</Td>
                  <Td className="font-medium">{saved.label ?? '—'}</Td>
                  <Td>
                    <Badge tone={RESULT_TONES[saved.result] ?? 'neutral'}>
                      {resultLabel(saved.result, t)}
                    </Badge>
                  </Td>
                  <Td className="text-ink-muted">{fmt.dateTime(saved.saved_at)}</Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setPendingDelete(saved.ref);
                      }}
                    >
                      {t('portal.codes.delete')}
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          <CardBody className="flex items-center justify-center border-t border-border">
            {hasMore ? (
              <Button size="sm" onClick={onLoadMore} disabled={isLoadingMore}>
                {isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
              </Button>
            ) : (
              <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
            )}
          </CardBody>
        </>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('portal.codes.delete.title')}
        detail={t('portal.codes.delete.detail')}
        confirmLabel={t('portal.codes.delete')}
        busy={remove.isPending}
        onCancel={() => {
          setPendingDelete(null);
        }}
        onConfirm={() => {
          if (pendingDelete !== null) remove.mutate(pendingDelete);
        }}
      />
    </Card>
  );
}

// ═══════════════════════════════════════════════ acceso y supresión

function RightsCard({
  token,
  onDeleteAccount,
  deleting,
  deleteError,
}: {
  token: string;
  onDeleteAccount: () => void;
  deleting: boolean;
  deleteError: ApiError | null;
}): ReactNode {
  const { t } = useI18n();
  const [confirming, setConfirming] = useState(false);

  const exportData = useMutation<PortalDataExport, ApiError>({
    mutationFn: () => portalExportData(token),
    onSuccess: (data) => {
      downloadJson(data, `xyvona-portal-${data.account.tenant}.json`);
    },
  });

  return (
    <Card>
      <CardHeader title={t('portal.rights.title')} />
      <CardBody className="space-y-5">
        <div className="space-y-2">
          <Button
            onClick={() => {
              exportData.mutate();
            }}
            disabled={exportData.isPending}
          >
            {t('portal.rights.export')}
          </Button>
          <p className="max-w-prose text-sm text-ink-muted">{t('portal.rights.exportHint')}</p>
          {exportData.isError && <ErrorState error={exportData.error} />}
        </div>

        <div className="space-y-2">
          <Button
            variant="critical"
            onClick={() => {
              setConfirming(true);
            }}
            disabled={deleting}
          >
            {t('portal.rights.delete')}
          </Button>
          <p className="max-w-prose text-sm text-ink-muted">{t('portal.rights.deleteHint')}</p>
          {deleteError !== null && <ErrorState error={deleteError} />}
        </div>
      </CardBody>

      <ConfirmDialog
        open={confirming}
        title={t('portal.delete.title')}
        detail={t('portal.delete.detail')}
        confirmLabel={t('portal.rights.delete')}
        confirmWord={t('portal.delete.confirmWord')}
        busy={deleting}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          setConfirming(false);
          onDeleteAccount();
        }}
      />
    </Card>
  );
}

function resultLabel(result: string, t: (key: MessageKey) => string): string {
  const key = RESULT_LABELS[result];
  return key === undefined ? result : t(key);
}

/**
 * El derecho de acceso se ejerce bajándose el archivo, no mirándolo en
 * pantalla. Se arma en el navegador a partir de la respuesta: la API ya
 * devolvió el JSON completo y mandarlo de nuevo como adjunto sólo agregaría
 * un endpoint.
 */
function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
